import { isEmpty, isNonEmpty } from '@sgme/fp';
import { forwardRef, type MouseEvent, type PropsWithChildren, type Ref } from 'react';
import { Dropdown, DropdownMenu, DropdownToggle } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { FormattedMessage } from 'react-intl';

import type { ProductType } from '@/api/user/getCurrentUser.ts';
import { isInstrumentChoiceAuthorized } from '@/App/tiles/ToggleProduct.tsx';
import { useUserContext } from '@/context/UserContext.tsx';
import { algoOrderTypes, limitOrdersTypes, type OrderType } from '@/store/orders/state.ts';
import type { ClientWay } from '@/store/orders/types.ts';

interface CreateOrderDropdownProps {
  clientWay: ClientWay;
  onClick: (orderType: OrderType, clientWay: ClientWay) => () => void;
}

export function CreateOrderDropdown({ clientWay, onClick }: CreateOrderDropdownProps) {
  const { currentUser } = useUserContext();

  const authorizedLimitOrders = limitOrdersTypes.filter((type) =>
    isInstrumentChoiceAuthorized(currentUser.productsAccess, type),
  );

  const authorizedAlgoOrders = algoOrderTypes.filter((type) =>
    isInstrumentChoiceAuthorized(currentUser.productsAccess, type),
  );

  if (isEmpty(authorizedLimitOrders) && isEmpty(authorizedAlgoOrders)) {
    return null;
  }

  return (
    <Dropdown drop={clientWay === 'buy' ? 'start' : 'end'}>
      <DropdownToggle as={CustomToggle} variant="link">
        {clientWay === 'buy' ? (
          <>
            <i className="icon">arrow_left</i>
            <FormattedMessage id="CashTile.callOrder.button.buy" />
          </>
        ) : (
          <>
            <FormattedMessage id="CashTile.callOrder.button.sell" />
            <i className="icon">arrow_right</i>
          </>
        )}
      </DropdownToggle>

      {createPortal(
        <DropdownMenu>
          {isNonEmpty(authorizedLimitOrders) && (
            <Dropdown.Header className="fw-semibold">
              <FormattedMessage id="order.form.orderType.group.Limit" />
            </Dropdown.Header>
          )}

          {isNonEmpty(authorizedLimitOrders) &&
            authorizedLimitOrders.map((order) => (
              <Dropdown.Item key={order} onClick={onClick(order, clientWay)}>
                <ProductMenuItemLabel productType={order} />
              </Dropdown.Item>
            ))}

          <Dropdown.Divider />

          {isNonEmpty(authorizedAlgoOrders) && (
            <Dropdown.Header className="fw-semibold">
              <FormattedMessage id="order.form.orderType.group.Algo" />
            </Dropdown.Header>
          )}

          {isNonEmpty(authorizedAlgoOrders) &&
            authorizedAlgoOrders.map((order) => (
              <Dropdown.Item key={order} onClick={onClick(order, clientWay)}>
                <ProductMenuItemLabel productType={order} />
              </Dropdown.Item>
            ))}
        </DropdownMenu>,
        document.body,
      )}
    </Dropdown>
  );
}

const CustomToggle = forwardRef(
  (
    { children, onClick }: PropsWithChildren<{ onClick: (event: MouseEvent<HTMLElement>) => void; className: string }>,
    ref: Ref<HTMLButtonElement> | undefined,
  ) => (
    <button
      type="button"
      className="btn btn-link fs-12 pb-4px"
      ref={ref}
      onClick={(event) => {
        event.preventDefault();
        onClick(event);
      }}
    >
      {children}
    </button>
  ),
);

const ProductMenuItemLabel = ({ productType, short = false }: { productType: ProductType; short?: boolean }) => (
  <FormattedMessage id={`productMenu.${productType}`} values={{ short }} />
);
